import { Badge, Group, Loader, Pagination, SegmentedControl, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listKnowledgeUnits, type KnowledgeUnitSummary } from '@mygpt/app/apis/services/knowledgeLoop';
import { UnitDrawer } from './UnitDrawer';
import { CARD_BORDER, formatJst, formatJstDate, unitStatusColor } from './shared';

const PAGE_SIZE = 30;
const FILTERS = {
  approved: 'approved',
  scheduled: 'scheduled',
  rejected: 'rejected',
  history: 'merged,superseded,stale',
} as const;
type Filter = keyof typeof FILTERS;

/** ナレッジ台帳：公開中の知識と、却下・統合された履歴。FAQ はここから投影されている */
export const Ledger = ({ datasetId }: { datasetId: string }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('approved');
  const [keyword, setKeyword] = useState('');
  const [q] = useDebouncedValue(keyword, 300);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<KnowledgeUnitSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listKnowledgeUnits(datasetId, {
        status: FILTERS[filter],
        q: q || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('load ledger failed:', e);
      showNotification({ color: 'red', message: t('knowledge_loop.load_error') });
    } finally {
      setLoading(false);
    }
  }, [datasetId, filter, q, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter, q]);

  return (
    <div style={{ border: CARD_BORDER, borderRadius: 12, background: '#fff' }}>
      <Group justify="space-between" p="md" style={{ borderBottom: CARD_BORDER }}>
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          data={(Object.keys(FILTERS) as Filter[]).map((f) => ({
            value: f,
            label: t(`knowledge_loop.ledger.filter_${f}`),
          }))}
        />
        <Group gap={8}>
          <Text size="xs" c="gray.6">
            {t('knowledge_loop.queue.total', { count: total })}
          </Text>
          <TextInput
            size="xs"
            w={220}
            leftSection={<IconSearch size={14} />}
            placeholder={t('knowledge_loop.ledger.search')}
            value={keyword}
            onChange={(e) => setKeyword(e.currentTarget.value)}
          />
        </Group>
      </Group>
      {loading ? (
        <Group justify="center" py={60}>
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Text size="sm" c="gray.5" ta="center" py={60}>
          {t('knowledge_loop.ledger.empty')}
        </Text>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table verticalSpacing={8} highlightOnHover style={{ fontVariantNumeric: 'tabular-nums' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('knowledge_loop.ledger.col_title')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_form')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_version')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_evidence')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_reviewer')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_verified')}</Table.Th>
                <Table.Th>{t('knowledge_loop.ledger.col_status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((u) => (
                <Table.Tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(u.id)}>
                  <Table.Td style={{ maxWidth: 420 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {u.title}
                    </Text>
                    <Text size="xs" c="gray.6" lineClamp={1}>
                      {u.body}
                    </Text>
                  </Table.Td>
                  <Table.Td>{t(`knowledge_loop.form.${u.publish_form}`)}</Table.Td>
                  <Table.Td>
                    {u.status === 'scheduled'
                      ? t('knowledge_loop.review.scheduled_for', { date: formatJstDate(u.effective_from) })
                      : u.status === 'rejected'
                      ? t('knowledge_loop.review.revision_count', { count: u.draft_revision || 0 })
                      : `v${u.version}`}
                  </Table.Td>
                  <Table.Td>{u.evidence_count}</Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {u.reviewed_by === 'auto'
                        ? t('knowledge_loop.ledger.reviewer_auto')
                        : u.reviewed_by === 'backfill'
                        ? t('knowledge_loop.ledger.reviewer_backfill')
                        : u.reviewed_by || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatJst(u.last_verified_at || u.reviewed_at)}</Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={unitStatusColor(u.status)}>
                      {t(`knowledge_loop.unit_status.${u.status}`)}
                    </Badge>
                    {!!u.pii_flagged && (
                      <Badge size="xs" variant="light" color="red" ml={4} style={{ textTransform: 'none' }}>
                        {t('knowledge_loop.pii.flagged_short')}
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <Group justify="center" p="md">
          <Pagination size="sm" value={page} onChange={setPage} total={Math.ceil(total / PAGE_SIZE)} />
        </Group>
      )}
      <UnitDrawer datasetId={datasetId} unitId={selected} onClose={() => setSelected(null)} onReviewed={load} />
    </div>
  );
};
